// The first step of selling a phone: pick it, one tap at a time (brand → model → storage → colour), from the device
// library, so the listing gets the exact model, the sizes and colours it really comes in, and a price reference.
// Every answer stays on screen as a chip that goes back to that question; the browser's Back button does the same.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { detectDevice, type Detected } from "../lib/deviceDetect";
import { searchDevices } from "../lib/deviceSearch";
import { useI18n } from "../lib/i18n";
import { useAsync } from "../lib/useAsync";
import { IconCheck, IconPhone, IconSearch, IconX } from "../ui/icons";
import { AsyncButton, Loading, Notice } from "../ui/kit";

export type DeviceColor = { en: string; ar: string; hex: string; image: string | null };
export type DeviceModelInfo = { model: string; storage: number[]; image: string | null; colors: DeviceColor[]; newPriceCents: Record<string, number> };
export type DeviceBrand = { brand: string; models: DeviceModelInfo[] };

type Screen = "brand" | "model" | "storage" | "color";
type Picks = { brand: string; model: string; storage: number | null; color: string };
type DraftLike = { id: string; category: string; specs: Record<string, { value: string }> };

/** Case, spaces and punctuation don't matter when matching names (same rule as the server). */
export const norm = (s?: string) => (s ?? "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
export const storageLabel = (gb: number) => (gb >= 1024 ? `${gb / 1024} TB` : `${gb} GB`);
export const storageGb = (label?: string) => {
  const m = (label ?? "").match(/([\d.]+)\s*(TB|GB)/i);
  return m ? Math.round(Number(m[1]) * (/tb/i.test(m[2]) ? 1024 : 1)) : null;
};

/** The phone line before its version: "iPhone 17 Pro Max" → "iPhone 17", "Galaxy S26+" → "Galaxy S26", "Pixel 7a" → "Pixel 7". */
function seriesOf(model: string) {
  const base = model.replace(/\s+(Pro Max|Pro\+|Pro|Plus|Ultra|Max|mini|FE|Lite|Neo|Edge)$/i, "").replace(/\+$/, "");
  return base.replace(/(\d+)[a-z]$/i, "$1");
}

export function useDeviceLibrary(category: string) {
  return useAsync(() => api<{ brands: DeviceBrand[] }>(`/api/catalog/devices?category=${category}`), [category]);
}

export function findModel(brands: DeviceBrand[], brand?: string, model?: string) {
  const b = brands.find((x) => norm(x.brand) === norm(brand));
  return { brandInfo: b, modelInfo: b?.models.find((m) => norm(m.model) === norm(model)) };
}

export function DeviceStep({ draft, onSaved, onManual }: { draft: DraftLike; onSaved: (d: any) => void; onManual: () => void }) {
  const { t, locale, money } = useI18n();
  const [params, setParams] = useSearchParams();
  const lib = useDeviceLibrary(draft.category);
  const brands = useMemo(() => lib.data?.brands ?? [], [lib.data]);
  const [picks, setPicks] = useState<Picks>(() => ({
    brand: draft.specs.brand?.value ?? "",
    model: draft.specs.model?.value ?? "",
    storage: storageGb(draft.specs.storage?.value),
    color: draft.specs.color?.value ?? "",
  }));
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [detected, setDetected] = useState<Detected | null>(null);
  const mine = useAsync(() => api<{ listings: { device: { brand: string; model: string } | null }[] }>("/api/listings/mine"), []);
  useEffect(() => {
    detectDevice().then(setDetected).catch(() => {});
  }, []);

  const { brandInfo, modelInfo } = findModel(brands, picks.brand, picks.model);
  const colorInfo = modelInfo?.colors.find((c) => norm(c.en) === norm(picks.color) || norm(c.ar) === norm(picks.color));

  // The screen in the URL, unless an earlier answer is missing (e.g. after a reload).
  const firstMissing: Screen = !brandInfo ? "brand" : !modelInfo ? "model" : !picks.storage || !modelInfo.storage.includes(picks.storage) ? "storage" : "color";
  const order: Screen[] = ["brand", "model", "storage", "color"];
  const asked = (params.get("pick") as Screen) ?? firstMissing;
  const screen = order.indexOf(asked) <= order.indexOf(firstMissing) ? asked : firstMissing;
  const show = (s: Screen) => {
    setQuery("");
    setError(null);
    const next = new URLSearchParams(params);
    next.set("pick", s);
    setParams(next);
  };

  if (lib.loading && !lib.data) return <Loading />;
  if (!brands.length) {
    // No library (or it failed to load): the details form works without it.
    return (
      <div className="stack">
        <Notice>{t("device.unavailable")}</Notice>
        <button className="btn primary block" onClick={onManual}>{t("common.continue")}</button>
      </div>
    );
  }

  const allModels = brands.flatMap((b) => b.models.map((m) => ({ brand: b.brand, model: m.model, info: m })));
  const label = (c: DeviceColor) => (locale === "ar" ? c.ar : c.en);
  const fromPrice = (m: DeviceModelInfo) => {
    const p = Object.values(m.newPriceCents ?? {});
    return p.length ? Math.min(...p) : null;
  };

  const pickModel = (brand: string, model: string) => {
    setPicks((p) => ({ brand, model, storage: norm(p.model) === norm(model) ? p.storage : null, color: norm(p.model) === norm(model) ? p.color : "" }));
    show("storage");
  };

  const save = async (color: string | null) => {
    setError(null);
    const specs: Record<string, string> = { brand: picks.brand, model: picks.model, storage: storageLabel(picks.storage!) };
    if (color !== null) specs.color = color;
    try {
      onSaved(await api(`/api/listings/${draft.id}`, { method: "PATCH", body: { specs } }));
    } catch (e) {
      setError((e as ApiError).message);
    }
  };

  // Answers so far: tap one to change it.
  const answers = (
    <div className="chips answer-chips" aria-label={t("device.yourPicks")}>
      {brandInfo && screen !== "brand" && <button type="button" className="chip removable" onClick={() => show("brand")}><bdi>{brandInfo.brand}</bdi></button>}
      {modelInfo && order.indexOf(screen) > 1 && <button type="button" className="chip removable" onClick={() => show("model")}><bdi>{modelInfo.model}</bdi></button>}
      {picks.storage && screen === "color" && <button type="button" className="chip removable" onClick={() => show("storage")}><bdi>{storageLabel(picks.storage)}</bdi></button>}
    </div>
  );

  const searchBox = (placeholder: string) => (
    <label className="search-field">
      <IconSearch size={18} aria-hidden="true" />
      <input className="input" type="search" dir="auto" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} aria-label={placeholder} autoComplete="off" enterKeyHint="search" />
      {query && <button type="button" className="btn ghost sm icon" onClick={() => setQuery("")} aria-label={t("device.clear")}><IconX size={16} /></button>}
    </label>
  );

  const modelRow = (brand: string, m: DeviceModelInfo, withBrand = false) => {
    const from = fromPrice(m);
    const on = norm(m.model) === norm(picks.model) && norm(brand) === norm(picks.brand);
    return (
      <button key={`${brand}:${m.model}`} type="button" className="pick-row" aria-pressed={on} onClick={() => pickModel(brand, m.model)}>
        <span className="t">
          <strong><bdi>{m.model}</bdi></strong>
          {withBrand && <span>{brand}</span>}
        </span>
        {from && <span className="muted small">{t("device.newFrom", { p: money(from, "SAR") })}</span>}
        {on && <IconCheck size={18} />}
      </button>
    );
  };

  const manualLink = (
    <button type="button" className="btn ghost block" onClick={onManual}>{t("device.manual")}</button>
  );

  if (screen === "brand") {
    const results = query.trim() ? searchDevices(allModels, query, 12) : [];
    const recent = [
      ...new Map(
        (mine.data?.listings ?? [])
          .map((l) => l.device && findModel(brands, l.device.brand, l.device.model))
          .filter((x): x is NonNullable<typeof x> & { modelInfo: DeviceModelInfo; brandInfo: DeviceBrand } => !!x?.modelInfo && !!x.brandInfo)
          .map((x) => [`${x.brandInfo.brand}:${x.modelInfo.model}`, x]),
      ).values(),
    ].slice(0, 4);
    const hit = detected && findModel(brands, detected.brand, detected.model ?? undefined);
    return (
      <div className="stack-lg">
        <h2 className="pick-q">{t("device.brandQ")}</h2>
        {searchBox(t("device.search"))}
        {query.trim() ? (
          results.length ? (
            <div className="pick-list">{results.map((r) => modelRow(r.brand, r.info, true))}</div>
          ) : (
            <Notice>{t("device.noResults")}</Notice>
          )
        ) : (
          <>
            {hit?.brandInfo && (
              <button
                type="button"
                className="detect-card"
                onClick={() => {
                  if (hit.modelInfo) pickModel(hit.brandInfo!.brand, hit.modelInfo.model);
                  else {
                    setPicks((p) => ({ ...p, brand: hit.brandInfo!.brand, model: norm(p.brand) === norm(hit.brandInfo!.brand) ? p.model : "" }));
                    show("model");
                  }
                }}
              >
                <IconPhone size={22} aria-hidden="true" />
                <span className="t">
                  <span>{t("device.thisPhone")}</span>
                  <strong><bdi>{hit.modelInfo ? hit.modelInfo.model : hit.brandInfo.brand}</bdi></strong>
                </span>
              </button>
            )}
            {recent.length > 0 && (
              <div>
                <div className="label" style={{ marginBottom: 6 }}>{t("device.recent")}</div>
                <div className="chips">
                  {recent.map((r) => (
                    <button key={r.modelInfo.model} type="button" className="chip" onClick={() => pickModel(r.brandInfo.brand, r.modelInfo.model)}><bdi>{r.modelInfo.model}</bdi></button>
                  ))}
                </div>
              </div>
            )}
            <div className="choice-grid">
              {brands.map((b) => (
                <button
                  key={b.brand}
                  type="button"
                  className="choice"
                  aria-pressed={norm(b.brand) === norm(picks.brand)}
                  onClick={() => {
                    setPicks((p) => (norm(p.brand) === norm(b.brand) ? p : { brand: b.brand, model: "", storage: null, color: "" }));
                    show("model");
                  }}
                >
                  <strong><bdi>{b.brand}</bdi></strong>
                  <span>{t("device.models", { n: b.models.length })}</span>
                </button>
              ))}
            </div>
          </>
        )}
        {manualLink}
      </div>
    );
  }

  if (screen === "model" && brandInfo) {
    const list = query.trim() ? searchDevices(brandInfo.models.map((m) => ({ brand: brandInfo.brand, model: m.model, info: m })), query, 40).map((r) => r.info) : brandInfo.models;
    const groups = new Map<string, DeviceModelInfo[]>();
    for (const m of list) groups.set(query.trim() ? "" : seriesOf(m.model), [...(groups.get(query.trim() ? "" : seriesOf(m.model)) ?? []), m]);
    return (
      <div className="stack-lg">
        {answers}
        <h2 className="pick-q">{t("device.modelQ")}</h2>
        {brandInfo.models.length > 6 && searchBox(t("device.searchModels", { brand: brandInfo.brand }))}
        {norm(brandInfo.brand) === "apple" && detected?.brand === "Apple" && <p className="small muted" style={{ margin: 0 }}>{t("device.iphoneTip")}</p>}
        {list.length ? (
          [...groups].map(([series, models]) => (
            <section key={series || "results"} className="pick-group">
              {/* A heading only where it groups several models ("iPhone 17": 17, 17 Pro, 17 Pro Max). */}
              {series && groups.size > 1 && models.length > 1 && <h3 className="series-head"><bdi>{series}</bdi></h3>}
              <div className="pick-list">{models.map((m) => modelRow(brandInfo.brand, m))}</div>
            </section>
          ))
        ) : (
          <Notice>{t("device.noResults")}</Notice>
        )}
        {manualLink}
      </div>
    );
  }

  if (screen === "storage" && modelInfo) {
    return (
      <div className="stack-lg">
        {answers}
        <h2 className="pick-q">{t("device.storageQ")}</h2>
        <div className="choice-grid">
          {modelInfo.storage.map((gb) => {
            const p = modelInfo.newPriceCents?.[String(gb)];
            return (
              <button
                key={gb}
                type="button"
                className="choice"
                aria-pressed={picks.storage === gb}
                onClick={() => {
                  setPicks((x) => ({ ...x, storage: gb }));
                  if (modelInfo.colors.length) show("color");
                }}
              >
                <strong><bdi>{storageLabel(gb)}</bdi></strong>
                {p ? <span>{t("device.newPrice", { p: money(p, "SAR") })}</span> : null}
              </button>
            );
          })}
        </div>
        {/* A model without colours in the library is done once the storage is picked. */}
        {!modelInfo.colors.length && picks.storage && (
          <div className="sticky-cta"><AsyncButton className="btn primary block" onClick={() => save(null)}>{t("common.continue")}</AsyncButton></div>
        )}
        {error && <Notice kind="bad">{error}</Notice>}
      </div>
    );
  }

  // Colour: tapping one saves the choices and moves on.
  return (
    <div className="stack-lg">
      {answers}
      <h2 className="pick-q">{t("device.colorQ")}</h2>
      <div className="choice-grid" role="radiogroup" aria-label={t("device.colorQ")}>
        {modelInfo!.colors.map((c) => (
          <button
            key={c.en}
            type="button"
            className="choice color-choice"
            role="radio"
            aria-checked={c === colorInfo}
            disabled={saving}
            onClick={async () => {
              setPicks((p) => ({ ...p, color: c.en }));
              setSaving(true);
              await save(c.en).finally(() => setSaving(false));
            }}
          >
            <span className="swatch big" style={{ background: c.hex }} aria-hidden="true" />
            <strong>{label(c)}</strong>
          </button>
        ))}
      </div>
      {error && <Notice kind="bad">{error}</Notice>}
      <AsyncButton className="btn ghost block" onClick={() => save(colorInfo ? colorInfo.en : null)}>
        {colorInfo ? t("common.continue") : t("device.skipColor")}
      </AsyncButton>
    </div>
  );
}
